import { Button } from '@bambi/component-library/src/components/Button/Button';
import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { currencyFormatter } from '../forms/cells/utils';
import { CurrencyInput } from 'src/common/CurrencyInput';
import type { InvoicePaymentRead } from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { Modal } from 'src/common/primitives/Modal';

interface RefundPaymentModalProps {
  open: boolean;
  payment: InvoicePaymentRead | null;
  setOpen: (open: boolean) => void;
  onConfirm: (amount_cents: number) => void;
}
export function RefundPaymentModal({
  open,
  payment,
  setOpen,
  onConfirm,
}: RefundPaymentModalProps) {
  const [loading, setLoading] = useState(false);
  const form = useForm<{ refund_amount: number }>({
    mode: 'all',
    defaultValues: {
      refund_amount: payment?.amount_total_cents,
    },
  });

  useEffect(() => {
    form.reset({
      refund_amount: payment?.amount_total_cents,
    });
  }, [form, payment?.amount_total_cents, open]);

  const handleSubmit = form.handleSubmit((values) => {
    setLoading(true);
    onConfirm(values.refund_amount);
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      contentClassnames="w-full max-w-[35vw] flex flex-col gap-4"
      testId="refund-modal"
      title="Refund Payment"
    >
      <h3 className="pb-2 text-[24px] font-medium leading-6 text-gray-900">
        Refund Payment
      </h3>
      {!payment ? (
        <FormErrorMessage>
          Something went wrong, unable to find payment.
        </FormErrorMessage>
      ) : (
        <>
          <p>
            You can refund either part of a payment or the full amount by
            modifying the amount field below. The refund amount cannot exceed
            the original payment.
          </p>
          <p>
            <strong>This action is not reversable.</strong> Refunds can take
            between 24-48 hours to process.
          </p>
          <Controller
            control={form.control}
            name="refund_amount"
            rules={{
              required: 'This field is required',
              min: 1,
              max: payment.amount_total_cents,
            }}
            render={({ field, fieldState }) => {
              const errorMessage = (() => {
                if (!fieldState.error) {
                  return '';
                }

                if (fieldState.error.message) {
                  return fieldState.error.message;
                }

                if (fieldState.error.type === 'max') {
                  return `Refund cannot be greater than ${currencyFormatter(
                    payment.amount_total_cents / 100
                  )}`;
                }

                if (fieldState.error.type === 'min') {
                  return 'Refund cannot be less than $0.01';
                }
              })();

              return (
                <DefaultFormFieldLayout
                  label="Refund Amount *"
                  error={errorMessage}
                  inputId="refund_amount"
                >
                  <CurrencyInput
                    value={field.value}
                    onValueChange={(value) => field.onChange(value.floatValue)}
                  />
                </DefaultFormFieldLayout>
              );
            }}
          />
        </>
      )}
      <div className="mt-4 flex flex-row justify-between">
        <Button
          dataTestId="cancelBtn"
          disabled={loading}
          onClick={() => setOpen(false)}
        >
          Cancel
        </Button>
        <Button
          dataTestId="confirmBtn"
          variant="primary-danger"
          disabled={!!Object.keys(form.formState.errors).length}
          loading={loading}
          onClick={handleSubmit}
        >
          Refund Payment
        </Button>
      </div>
    </Modal>
  );
}
